import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { DocumentNode, useQuery } from "@apollo/client";
import { BusinessBasic, BusinessData } from "../../types";
import { addBusinesses } from "../actions/businessActions";
import {
  BusinessQueryNames,
  GET_BUSINESSES,
  GET_MY_BUSINESSES,
} from "../../graphql/queries";
import { useAuthStatus } from "../../util/useAuthStatus";

const queryDocuments: { [key in BusinessQueryNames]: DocumentNode } = {
  [BusinessQueryNames.GET_MY_BUSINESSES]: GET_MY_BUSINESSES,
  [BusinessQueryNames.GET_BUSINESSES]: GET_BUSINESSES,
};

export const normalizeBusinessData = (input: any | any[]): BusinessData[] => {
  const dataList = Array.isArray(input) ? input : [input];

  return dataList.map((business) => {
    const {
      __typename,
      id,
      businessCategoryId,
      name,
      description,
      currency,
      status,
      street,
      cityId,
      countryId,
      stateId,
      zipcode,
      address,
      lat,
      lng,
      hasLocation,
      phoneNumbers,
      emails,
      url,
      links,
      images,
      staffs,
      stage,
      createdBy,
      roles,
      plans,
      displayOnBookingPage,
      businessAddress,
      workingHourGroups,
      businessBookingTypes,
      ...rest
    } = business;

    const businessBasic: BusinessBasic = {
      id: Number(id),
      businessCategoryId: Number(businessCategoryId),
      name,
      description,
      currency,
      status,
      street,
      cityId: Number(cityId),
      countryId: Number(countryId),
      stateId: Number(stateId),
      zipcode,
      address,
      lat,
      lng,
      hasLocation,
      stage,
      createdBy: Number(createdBy),
      phoneNumbers:
        phoneNumbers?.map((p: any) => ({
          countryCode: p.countryCode,
          phoneNumber: p.phoneNumber,
        })) ?? [],
      links:
        links?.map((l: any) => ({
          platform: l.platform,
          url: l.url,
        })) ?? [],
      emails,
      url,
      images,
      displayOnBookingPage,
    };

    return {
      businessBasic,
      businessStaffs: staffs ?? [],
      roles:
        roles?.map((r: any) => ({
          id: Number(r.id),
          name: r.name,
          description: r.description,
          permissions: JSON.stringify(r.permissions),
        })) ?? [],
      plans:
        plans?.map((p: any) => ({
          id: Number(p.id),
          name: p.name,
          priceMonthly: p.priceMonthly,
          priceYearly: p.priceYearly,
          features: p.features,
        })) ?? [],
      businessAddress: businessAddress ?? null,
      workingHourGroups: workingHourGroups ?? [],
      businessBookingTypes: businessBookingTypes ?? [],
      ...rest,
    };
  });
};

const useLoadReduxStoreData = (
  queryName: BusinessQueryNames,
  variables: any = {}
) => {
  const dispatch = useDispatch();
  const queryDocument = queryDocuments[queryName];
  const { enabled = true, ...queryVariables } = variables || {};
  const { isLoggedIn } = useAuthStatus();

  const skipQuery =
    !enabled ||
    (queryName === BusinessQueryNames.GET_MY_BUSINESSES && !isLoggedIn);

  const { loading, error, data, refetch } = useQuery(queryDocument, {
    variables: queryVariables,
    skip: skipQuery,
  });

  useEffect(() => {
    if (!loading && !error && data) {
      let businessResult;

      switch (queryName) {
        case BusinessQueryNames.GET_BUSINESSES:
          businessResult = data.businesses;
          break;
        case BusinessQueryNames.GET_MY_BUSINESSES:
          businessResult = data.myBusinesses;
          break;
        default:
          businessResult = null;
      }

      if (businessResult) {
        const businesses = normalizeBusinessData(businessResult);

        if (queryName === BusinessQueryNames.GET_MY_BUSINESSES) {
          dispatch(addBusinesses(businesses, "MY_BUSINESSES"));
        } else if (queryName === BusinessQueryNames.GET_BUSINESSES) {
          dispatch(addBusinesses(businesses, "ALL_BUSINESSES"));
        }
      }
    }

    // ✅ Only log error once loading is complete
    if (!loading && error) {
      console.error("Error loading data:", error.message);
    }
  }, [loading, error, data, dispatch, queryName]);

  return { loading, error, refetch };
};

export default useLoadReduxStoreData;
